import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React, { useCallback, useEffect, useRef } from 'react';
import { View, Text, TextInput } from 'react-native';
import styled from 'styled-components/native';
import SccPressable from '@/components/SccPressable';
import SccRemoteImage from '@/components/SccRemoteImage';
import { color } from '@/constant/color';
import { useLogger } from '@/logging/useLogger';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import ImageUploader from '../components/ImageUploader';
import { useEditMode } from '../context/EditModeContext';
import { useResponsive } from '../context/ResponsiveContext';
export default function OverviewSection({ overviewSection, sectionId, }) {
    var _a;
    const editContext = useEditMode();
    const isEditMode = (_a = editContext === null || editContext === void 0 ? void 0 : editContext.isEditMode) !== null && _a !== void 0 ? _a : false;
    const { isDesktop } = useResponsive();
    const logger = useLogger();
    const loggerRef = useRef(logger);
    loggerRef.current = logger;
    const { titleLine1, titleLine2, mapImageUrl, mobileMapImageUrl } = overviewSection;
    // 데스크탑이 아니면 모바일 이미지 우선 사용
    const displayMapImageUrl = isDesktop
        ? mapImageUrl
        : (mobileMapImageUrl || mapImageUrl);
    const updateOverviewSection = useCallback((updates) => {
        if (!editContext)
            return;
        editContext.updateData((prev) => (Object.assign(Object.assign({}, prev), { overviewSection: prev.overviewSection
                ? Object.assign(Object.assign({}, prev.overviewSection), updates) : null })));
    }, [editContext]);
    const handleTitleLine1Change = useCallback((text) => {
        updateOverviewSection({ titleLine1: text });
    }, [updateOverviewSection]);
    const handleTitleLine2Change = useCallback((text) => {
        updateOverviewSection({ titleLine2: text });
    }, [updateOverviewSection]);
    const handleMapImageChange = useCallback((url) => {
        updateOverviewSection({ mapImageUrl: url });
    }, [updateOverviewSection]);
    // 이미지 노출 로깅
    useEffect(() => {
        if (!isEditMode && displayMapImageUrl) {
            loggerRef.current.logElementView('bbucle-road-overview-map-image', {
                imageUrl: displayMapImageUrl,
                isDesktop,
            });
        }
    }, [displayMapImageUrl, isEditMode, isDesktop]);
    return (_jsx(LogParamsProvider, { params: { displaySectionName: '한눈에보기' }, children: _jsx("div", { id: sectionId, children: _jsx(Container, { isDesktop: isDesktop, children: _jsxs(ContentWrapper, { isDesktop: isDesktop, children: [_jsx(TitleSection, { children: isEditMode ? (_jsxs(_Fragment, { children: [_jsx(TitleLine1Input, { value: titleLine1, onChangeText: handleTitleLine1Change, placeholder: "\uD0C0\uC774\uD2C0 1\uC904 (\uAC80\uC815)", placeholderTextColor: color.gray40, isDesktop: isDesktop }), _jsx(TitleLine2Input, { value: titleLine2, onChangeText: handleTitleLine2Change, placeholder: "\uD0C0\uC774\uD2C0 2\uC904 (\uD30C\uB791)", placeholderTextColor: color.gray40, isDesktop: isDesktop })] })) : (_jsxs(_Fragment, { children: [_jsx(TitleLine1, { isDesktop: isDesktop, children: titleLine1 }), _jsx(TitleLine2, { isDesktop: isDesktop, children: titleLine2 })] })) }), _jsxs(MapSection, { isDesktop: isDesktop, children: [mapImageUrl ? (_jsxs(ImageContainer, { isDesktop: isDesktop, children: [_jsx(SccPressable, { onPress: () => { }, elementName: "bbucle-road-overview-map-image-click", logParams: { imageUrl: displayMapImageUrl }, disableLogging: isEditMode, children: _jsx(SccRemoteImage, { imageUrl: mapImageUrl, resizeMode: "contain", style: { borderRadius: 12 }, wrapperBackgroundColor: "white" }) }), isEditMode && (_jsx(ImageOverlay, { children: _jsx(ImageUploader, { currentImageUrl: mapImageUrl, onUploadComplete: handleMapImageChange, compact: true }) }))] })) : (isEditMode && (_jsxs(EmptyImagePlaceholder, { children: [_jsx(EmptyImageText, { children: "\uC9C0\uB3C4 \uC774\uBBF8\uC9C0\uB97C \uC5C5\uB85C\uB4DC\uD558\uC138\uC694" }), _jsx(ImageUploader, { onUploadComplete: handleMapImageChange, buttonText: "\uC774\uBBF8\uC9C0 \uC5C5\uB85C\uB4DC" })] }))), _jsx(HintText, { isDesktop: isDesktop, children: "*\uB354 \uC790\uC138\uD55C \uB0B4\uC6A9\uC740 \uC544\uB798\uC5D0\uC11C \uD655\uC778\uD574\uC8FC\uC138\uC694" })] })] }) }) }) }));
}
const Container = styled(View) `
  padding-top: ${({ isDesktop }) => (isDesktop ? '120px' : '60px')};
  padding-bottom: ${({ isDesktop }) => (isDesktop ? '120px' : '80px')};
  width: 100%;
`;
const ContentWrapper = styled(View) `
  align-items: center;
  gap: ${({ isDesktop }) => (isDesktop ? '60px' : '40px')};
  width: 100%;
  padding: 0 16px;
`;
const TitleSection = styled(View) `
  align-items: center;
  gap: 0;
  max-width: 800px;
  width: 100%;
  padding: 0 16px;
`;
const TitleLine1 = styled(Text) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '36px' : '24px')};
  font-weight: 700;
  color: ${color.black};
  text-align: center;
  line-height: ${({ isDesktop }) => (isDesktop ? '48px' : '34px')};
`;
const TitleLine2 = styled(Text) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '36px' : '24px')};
  font-weight: 700;
  color: #0e64d3;
  text-align: center;
  line-height: ${({ isDesktop }) => (isDesktop ? '48px' : '34px')};
`;
const TitleLine1Input = styled(TextInput) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '40px' : '24px')};
  font-weight: 700;
  color: ${color.black};
  text-align: center;
  width: 100%;
`;
const TitleLine2Input = styled(TextInput) `
  font-family: Pretendard;
  font-size: ${({ isDesktop }) => (isDesktop ? '40px' : '24px')};
  font-weight: 700;
  color: #0e64d3;
  text-align: center;
  width: 100%;
`;
const ImageContainer = styled(View) `
  max-width: 1020px;
  width: ${({ isDesktop }) => (isDesktop ? '100%' : 'calc(100% + 32px)')};
  margin-horizontal: ${({ isDesktop }) => (isDesktop ? '0' : '-16px')};
  position: relative;
`;
const ImageOverlay = styled(View) `
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
`;
const EmptyImagePlaceholder = styled(View) `
  max-width: 1020px;
  width: 100%;
  padding: 60px;
  background-color: ${color.white};
  border: 2px dashed ${color.gray25};
  border-radius: 12px;
  align-items: center;
  gap: 16px;
`;
const EmptyImageText = styled(Text) `
  font-size: 16px;
  color: ${color.gray60};
`;
const MapSection = styled(View) `
  width: 100%;
  max-width: 800px;
  gap: 12px;
`;
const HintText = styled(Text) `
  font-size: ${({ isDesktop }) => (isDesktop ? '16px' : '12px')};
  line-height: ${({ isDesktop }) => (isDesktop ? '24px' : '16px')};
  font-weight: 400;
  color: ${color.gray50};
  padding: 0 ${({ isDesktop }) => (isDesktop ? '0' : '0')};
`;
